import React from "react";
import { Box, Tab, Tabs } from "@mui/material";
import BatchStatusIcon, { STATUS_META } from "./BatchStatusIcon";
import TabRemoveButton from "./TabRemoveButton";

/**
 * The middle of the batch editor's single header row (BatchEditorHeader owns
 * the row): the open tab's template on the left, the selected video's
 * platforms to its right, and the tab's own controls at the far end.
 * The two ends are slots (`leadingRef` / `trailingRef`) the editor pane fills,
 * since that is where the template and video choice live.
 *
 * Arrow keys move focus and Enter switches (MUI's default manual activation):
 * a switch reloads the editor, so merely moving focus must not trigger one.
 */
export default function PlatformTabBar({ video, activePlatform, statusOf, onSelect, onRemove, leadingRef, trailingRef }) {
  if (!video) return <Box flex={1} />;
  // Every platform tab at its natural width, so the controls beside them wrap
  // to the next line instead of hiding tabs behind scroll arrows (user report
  // 2026-10-09). Only an unusually long list scrolls.
  const scrolls = video.items.length > 6;
  return (
    <>
      <Box ref={leadingRef} sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0, maxWidth: { xs: 180, md: 320 }, minWidth: 0, "&:empty": { display: "none" } }} />
      <Tabs
        value={activePlatform || false}
        onChange={(e, platform) => onSelect(platform)}
        variant={scrolls ? "scrollable" : "standard"}
        scrollButtons={scrolls ? "auto" : false}
        aria-label={`Platforms for ${video.video.unique_key}`}
        sx={{
          minHeight: 30,
          flex: scrolls ? 1 : "0 0 auto",
          minWidth: scrolls ? 320 : "auto",
          // Wrapping row (below laptop width): keep room for at least one tab.
          "& .MuiTabs-indicator": { bgcolor: "#4338ca", height: 3, borderRadius: "3px 3px 0 0" },
        }}
      >
        {video.items.map((item) => {
          const status = statusOf(item);
          return (
            <Tab
              key={item.platform}
              value={item.platform}
              disableRipple
              aria-label={`${item.platform}, ${STATUS_META[status].label}`}
              // No Delete/Backspace shortcut (user report 2026-10-09): focus
              // stays on the tab while the canvas is used, so deleting a
              // caption also removed the platform. The × removes it.
              label={
                <Box display="flex" alignItems="center" gap={0.75}>
                  <BatchStatusIcon status={status} />
                  <span>{item.platform}</span>
                  <TabRemoveButton label={`Remove ${item.platform} of ${item.wgid} from the batch editor`} onRemove={() => onRemove(item)} />
                </Box>
              }
              sx={{
                minHeight: 30,
                py: 0.25,
                px: 1,
                textTransform: "none",
                fontWeight: 600,
                fontSize: 11.5,
                color: "#475569",
                "&.Mui-selected": { color: "#312e81" },
                "&.Mui-focusVisible": { outline: "2px solid #4338ca", outlineOffset: -2, borderRadius: 1 },
              }}
            />
          );
        })}
      </Tabs>
      <Box ref={trailingRef} sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0, ml: "auto", "&:empty": { display: "none" } }} />
    </>
  );
}
